import * as THREE from "three";
import type { LoadedModel } from "./loader";

const EXPLOSION_SCALE_FACTOR = 0.85; // multiplier on sceneRadius

/**
 * Computes per-mesh explosion offsets given a 0..1 explosion progress.
 * Offsets are in the mesh's *parent-local* space so they compose with the
 * baseLocalPos that HoverIsolate stores.
 */
export class ExplodeController {
  private dirsLocal = new Map<THREE.Mesh, THREE.Vector3>();
  private maxOffsets = new Map<THREE.Mesh, number>();
  private current = 0;
  private target = 0;

  constructor(model: LoadedModel) {
    const scale = model.sceneRadius * EXPLOSION_SCALE_FACTOR;
    for (const [, entry] of model.parts) {
      // Compute one direction PER-PART (group), so all sub-meshes of a part
      // travel together and stay visually coherent.
      const partCenter = new THREE.Vector3();
      let count = 0;
      const tmp = new THREE.Vector3();
      for (const m of entry.meshes) {
        m.getWorldPosition(tmp);
        partCenter.add(tmp);
        count++;
      }
      if (count === 0) continue;
      partCenter.divideScalar(count);
      const dirWorld = partCenter.clone().sub(model.sceneCenter);
      if (dirWorld.lengthSq() < 1e-6) dirWorld.set(0, 1, 0);
      const dist = Math.max(0.4, dirWorld.length() / Math.max(0.001, model.sceneRadius));
      dirWorld.normalize();

      for (const mesh of entry.meshes) {
        const parent = mesh.parent ?? model.root;
        const parentInv = parent.getWorldQuaternion(new THREE.Quaternion()).invert();
        const local = dirWorld.clone().applyQuaternion(parentInv).normalize();
        this.dirsLocal.set(mesh, local);
        this.maxOffsets.set(mesh, scale * dist);
      }
    }
  }

  setTarget(t: number) {
    this.target = Math.max(0, Math.min(1, t));
  }

  /** Called once per frame; returns the per-mesh offset map (caller passes to HoverIsolate). */
  tick(dt: number, into: Map<THREE.Mesh, THREE.Vector3>) {
    const halfLife = 0.18;
    const k = 1 - Math.pow(2, -dt / halfLife);
    this.current += (this.target - this.current) * k;
    if (Math.abs(this.target - this.current) < 0.0008) this.current = this.target;

    for (const [mesh, dir] of this.dirsLocal) {
      const max = this.maxOffsets.get(mesh) ?? 0;
      const off = into.get(mesh) ?? new THREE.Vector3();
      off.copy(dir).multiplyScalar(this.current * max);
      into.set(mesh, off);
    }
  }

  get value() {
    return this.current;
  }
}
